import React, { useState } from 'react';
import { Calendar, X, MousePointerClick } from 'lucide-react';
import { MONTH_NAMES, DAY_NAMES, STATI_BLOCCANTI } from '../config/constants';
import { isPastDate, getPriceForDate, getEffectiveEndDate, parseLocalDate } from '../utils/helpers';
import { useToast } from './ToastProvider';

const AvailabilityCalendar = ({ car, allBookings, onSelectDates }) => {
  const toast = useToast();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectingStart, setSelectingStart] = useState(true);
  const [tempStart, setTempStart] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  // Stato per il range CONFERMATO (entrambe le date selezionate)
  const [confirmedStart, setConfirmedStart] = useState(null);
  const [confirmedEnd, setConfirmedEnd] = useState(null);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const rawStart = firstDay.getDay();
    const startingDayOfWeek = rawStart === 0 ? 6 : rawStart - 1;
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const isDateBooked = (date) => {
    const checkDate = parseLocalDate(date);
    checkDate.setHours(0, 0, 0, 0);
    return allBookings.some(booking => {
      if (booking.carId !== car.id) return false;
      if (!STATI_BLOCCANTI.includes(booking.stato)) return false;
      // BUG FIX (fuso orario): vedi parseLocalDate in helpers.js — evita che
      // su un fuso orario negativo rispetto a UTC un giorno realmente libero
      // appaia come "già prenotato" sul calendario pubblico.
      const bookingStart = parseLocalDate(booking.dataInizio);
      bookingStart.setHours(0, 0, 0, 0);
      const bookingEnd = parseLocalDate(getEffectiveEndDate(booking));
      bookingEnd.setHours(0, 0, 0, 0);
      return checkDate >= bookingStart && checkDate <= bookingEnd;
    });
  };

  const rangeHasConflict = (startStr, endStr) => {
    const start = parseLocalDate(startStr);
    const end = parseLocalDate(endStr);
    const current = new Date(start);
    while (current < end) {
      if (isDateBooked(current)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  const dateToStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleDateClick = (date) => {
    if (isPastDate(date) || isDateBooked(date)) return;
    const dateStr = dateToStr(date);

    if (selectingStart) {
      setTempStart(dateStr);
      setConfirmedStart(null);
      setConfirmedEnd(null);
      setSelectingStart(false);
      onSelectDates(dateStr, null);
    } else {
      if (new Date(dateStr) < new Date(tempStart)) {
        // Clicca prima dello start: ricomincia
        setTempStart(dateStr);
        setConfirmedStart(null);
        setConfirmedEnd(null);
        onSelectDates(dateStr, null);
      } else if (rangeHasConflict(tempStart, dateStr)) {
        toast.warning('Il periodo selezionato contiene date non disponibili. Seleziona un intervallo diverso.');
        setTempStart(null);
        setConfirmedStart(null);
        setConfirmedEnd(null);
        setSelectingStart(true);
        onSelectDates(null, null);
      } else {
        // Range confermato: salva e mostra l'evidenziazione permanente
        setConfirmedStart(tempStart);
        setConfirmedEnd(dateStr);
        setTempStart(null);
        setHoveredDate(null);
        setSelectingStart(true);
        onSelectDates(tempStart, dateStr);
      }
    }
  };

  // Range durante la selezione (hover preview)
  const isInPreviewRange = (dateStr) => {
    if (!tempStart) return false;
    const end = hoveredDate;
    if (!end) return false;
    const a = new Date(tempStart);
    const b = new Date(end);
    const d = new Date(dateStr);
    if (a >= b) return false;
    return d > a && d < b;
  };

  // Range confermato (dopo aver scelto entrambe le date)
  const isInConfirmedRange = (dateStr) => {
    if (!confirmedStart || !confirmedEnd) return false;
    const a = new Date(confirmedStart);
    const b = new Date(confirmedEnd);
    const d = new Date(dateStr);
    return d > a && d < b;
  };

  const prevMonth = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() - 1);
    const nowMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (d >= nowMonth) setCurrentMonth(d);
  };

  const nextMonth = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + 1);
    setCurrentMonth(d);
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);

  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(<div key={`empty-${i}`} className="h-12 sm:h-16"></div>);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dateStr = dateToStr(date);

    const booked = isDateBooked(date);
    const past = isPastDate(date);
    const disabled = booked || past;

    const isConfirmedStart = dateStr === confirmedStart;
    const isConfirmedEnd = dateStr === confirmedEnd;
    const inConfirmedRange = isInConfirmedRange(dateStr);

    const isTempStart = dateStr === tempStart;
    const inPreviewRange = isInPreviewRange(dateStr);

    // La data hoveredEnd durante la preview
    const isHoveredEnd = !selectingStart && dateStr === hoveredDate && hoveredDate > tempStart;

    const price = getPriceForDate(car, date);

    // Calcolo classi CSS
    let cellClass = 'h-12 sm:h-16 text-xs sm:text-sm rounded-lg flex flex-col items-center justify-center p-0.5 sm:p-1 transition-all relative ';

    if (disabled) {
      cellClass += 'bg-gray-100 text-gray-300 cursor-not-allowed ';
    } else if (isConfirmedStart || isConfirmedEnd) {
      // Estremità del range confermato: blu pieno
      cellClass += 'bg-blue-600 text-white shadow-md ring-2 ring-blue-400 ring-offset-1 cursor-pointer ';
    } else if (inConfirmedRange) {
      // Interno del range confermato: azzurro pieno visibile
      cellClass += 'bg-blue-200 text-blue-900 cursor-pointer ';
    } else if (isTempStart) {
      // Start temporaneo durante la selezione
      cellClass += 'bg-blue-500 text-white shadow cursor-pointer ';
    } else if (isHoveredEnd) {
      // Fine provvisoria durante hover
      cellClass += 'bg-blue-400 text-white cursor-pointer ';
    } else if (inPreviewRange) {
      // Preview range durante hover
      cellClass += 'bg-blue-100 text-blue-800 cursor-pointer ';
    } else {
      // Giorno libero normale
      cellClass += 'hover:bg-blue-50 hover:shadow-sm cursor-pointer ';
    }

    days.push(
      <button
        key={day}
        type="button"
        onClick={() => handleDateClick(date)}
        onMouseEnter={() => { if (!selectingStart) setHoveredDate(dateStr); }}
        onMouseLeave={() => setHoveredDate(null)}
        disabled={disabled}
        className={cellClass}
      >
        <span className="font-semibold leading-none">{day}</span>
        {!disabled && (
          <span className={`text-xs mt-0.5 font-medium leading-none ${
            isConfirmedStart || isConfirmedEnd || isTempStart || isHoveredEnd
              ? 'text-white opacity-90'
              : inConfirmedRange
              ? 'text-blue-700'
              : 'text-green-600'
          }`}>
            €{price.toFixed(0)}
          </span>
        )}
        {booked && (
          <span className="text-xs text-red-400 leading-none">●</span>
        )}
      </button>
    );
  }

  const isPrevDisabled = () => {
    const nowMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return currentMonth <= nowMonth;
  };

  return (
    <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-xl">
      {/* Navigazione mese */}
      <div className="flex justify-between items-center mb-3">
        <button
          type="button"
          onClick={prevMonth}
          disabled={isPrevDisabled()}
          className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-600 font-bold"
        >
          ←
        </button>
        <h3 className="font-bold text-gray-800 text-sm sm:text-base">
          {MONTH_NAMES[month]} {year}
        </h3>
        <button
          type="button"
          onClick={nextMonth}
          className="w-8 h-8 flex items-center justify-center bg-white border border-gray-200 rounded-lg hover:bg-gray-100 text-gray-600 font-bold"
        >
          →
        </button>
      </div>

      {/* Intestazioni giorni */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_NAMES.map(name => (
          <div key={name} className="text-center text-xs font-semibold text-gray-500 py-1">
            {/* Su mobile mostra solo la prima lettera */}
            <span className="hidden sm:inline">{name}</span>
            <span className="sm:hidden">{name.charAt(0)}</span>
          </div>
        ))}
      </div>

      {/* Griglia giorni */}
      <div className="grid grid-cols-7 gap-1">
        {days}
      </div>

      {/* Riepilogo selezione attuale */}
      {(confirmedStart && confirmedEnd) && (
        <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="text-xs text-blue-800">
            <span className="font-semibold inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />Periodo selezionato:</span>{' '}
            {parseLocalDate(confirmedStart).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
            {' → '}
            {parseLocalDate(confirmedEnd).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
          </div>
          <button
            type="button"
            onClick={() => {
              setConfirmedStart(null);
              setConfirmedEnd(null);
              setTempStart(null);
              setSelectingStart(true);
              onSelectDates(null, null);
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-2 flex-shrink-0 inline-flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" />Cambia
          </button>
        </div>
      )}

      {/* Legenda + istruzione */}
      <div className="mt-3 space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-blue-600 rounded shadow-sm ring-1 ring-blue-400"></div>
            <span>Inizio / Fine</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-blue-200 border border-blue-300 rounded"></div>
            <span>Periodo</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-gray-100 rounded"></div>
            <span>Non disp.</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-green-600 font-semibold">€XX</span>
            <span>Prezzo/giorno</span>
          </div>
        </div>
        <p className={`text-xs font-semibold flex items-center gap-1.5 ${selectingStart ? 'text-gray-500' : 'text-blue-600'}`}>
          {selectingStart && !confirmedStart
            ? <><MousePointerClick className="w-3.5 h-3.5 shrink-0" />Clicca una data per iniziare la selezione</>
            : !selectingStart
            ? <><MousePointerClick className="w-3.5 h-3.5 shrink-0" />Ora clicca la data di fine noleggio</>
            : null
          }
        </p>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
